import type { AgentTemplate } from "../types";
import { defineComponent } from "vue";
import Badge from "@/components/ui/badge/Badge";
import Button from "@/components/ui/button/Button";
import Icon from "@/components/ui/icon/Icon";
import { FALLBACK_LLM_MODEL, useDefaultModels } from "@/composables/useDefaultModels";
import { $t } from "@/services/i18n";
import { p } from "@/utils/props-builder";
import { TEMPLATE_CATEGORIES } from "../categories";
import DifficultyBadge from "./DifficultyBadge";

const props = {
  template: p.$object<AgentTemplate>(),
  onBack: p.$func<() => void>(),
  onApply: p.$func<() => void>(),
  isApplying: p.boolean(false),
};

export default defineComponent({
  name: "TemplatePreview",
  props,
  setup(props) {
    const { defaultLlmModel } = useDefaultModels();

    return () => {
      const template = props.template;
      const categoryMeta = TEMPLATE_CATEGORIES[template.category];
      // Templates não declaram mais llm_model; mostramos o modelo default resolvido.
      const displayModel =
        template.config.llm_model ?? defaultLlmModel.value ?? FALLBACK_LLM_MODEL;

      return (
        <div class="flex h-full w-full flex-col">
          {/* Header com botão voltar */}
          <div class="shrink-0 border-b p-4">
            <Button variant="text" size="sm" icon="lucide:arrow-left" onClick={props.onBack}>
              {$t("goBack")}
            </Button>
          </div>

          {/* Conteúdo scrollável */}
          <div class="flex-1 overflow-y-auto p-6">
            {/* Cabeçalho do template */}
            <div class="mb-6 flex items-start gap-4">
              <div class="rounded-xl bg-accent-3 p-4" data-accent={categoryMeta.color}>
                <Icon icon={template.icon} class="h-10 w-10 text-accent-9" />
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <h2 class="text-xl font-semibold">{$t(template.nameKey as any)}</h2>
                  <DifficultyBadge difficulty={template.difficulty} />
                </div>
                <p class="mt-1 text-muted-foreground">{$t(template.descriptionKey as any)}</p>
                <Badge color={categoryMeta.color} variant="subtle" class="mt-2">
                  {$t(categoryMeta.nameKey as any)}
                </Badge>
              </div>
            </div>

            {/* Descrição detalhada */}
            <div class="mb-6">
              <h3 class="mb-2 font-semibold">{$t("agents:templates.about")}</h3>
              <p class="text-sm text-muted-foreground">{$t(template.detailsKey as any)}</p>
            </div>

            {/* Casos de uso */}
            <div class="mb-6">
              <h3 class="mb-2 font-semibold">{$t("agents:templates.use_cases")}</h3>
              <ul class="space-y-2">
                {template.useCasesKeys.map((key: string) => (
                  <li key={key} class="flex items-start gap-2 text-sm">
                    <Icon icon="lucide:check" class="text-green-500 mt-0.5 h-4 w-4 shrink-0" />
                    <span>{$t(key as any)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Highlights */}
            <div class="mb-6">
              <h3 class="mb-2 font-semibold">{$t("agents:templates.highlights")}</h3>
              <ul class="space-y-2">
                {template.highlightsKeys.map((key: string) => (
                  <li key={key} class="flex items-start gap-2 text-sm">
                    <Icon icon="lucide:sparkles" class="text-amber-500 mt-0.5 h-4 w-4 shrink-0" />
                    <span>{$t(key as any)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Requer configuração externa */}
            {template.setupNotesKeys && template.setupNotesKeys.length > 0 && (
              <div class="mb-6">
                <h3 class="mb-2 font-semibold">
                  {$t("agents:templates.preview.setup_notes_title")}
                </h3>
                <ul class="space-y-2">
                  {template.setupNotesKeys.map((key: string) => (
                    <li key={key} class="flex items-start gap-2 text-sm">
                      <Icon
                        icon="lucide:settings-2"
                        class="text-blue-500 mt-0.5 h-4 w-4 shrink-0"
                      />
                      <span>{$t(key as any)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Configurações */}
            <div class="rounded-lg bg-muted/50 p-4">
              <h3 class="mb-3 font-semibold">{$t("agents:templates.configurations")}</h3>
              <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt class="text-muted-foreground">{$t("agents:templates.model")}</dt>
                <dd class="font-medium">{displayModel}</dd>

                <dt class="text-muted-foreground">{$t("language")}</dt>
                <dd class="font-medium">{template.config.language?.toUpperCase()}</dd>

                {template.config.tools && template.config.tools.length > 0 && (
                  <>
                    <dt class="text-muted-foreground">{$t("agents:templates.tools_included")}</dt>
                    <dd class="font-medium">{template.config.tools.length}</dd>
                  </>
                )}

                {template.analysisDependencies && template.analysisDependencies.length > 0 && (
                  <>
                    <dt class="text-muted-foreground">
                      {$t("agents:templates.analyses_included")}
                    </dt>
                    <dd class="font-medium">{template.analysisDependencies.length}</dd>
                  </>
                )}
              </dl>
            </div>
          </div>

          {/* Footer com botão de aplicar */}
          <div class="shrink-0 border-t p-4">
            <Button
              block
              color="green"
              icon="lucide:check"
              onClick={props.onApply}
              loading={props.isApplying}
            >
              {$t("agents:templates.use_template")}
            </Button>
          </div>
        </div>
      );
    };
  },
});
